import { Ref } from 'vue';
import { computed } from 'vue';

import { useQuery, useMutation, useQueries } from '@tanstack/vue-query';
import { QueryObserverResult } from '@tanstack/vue-query';

import moment from 'moment';

import { Apis, IdLiteral } from '@/constants/api';
import {
  SettlementHeaderSchema,
  plantsLocationArray,
  settlementContractSchema,
  postSettlementContractSchema,
  PdfInfoSchema,
  companyPaymentSchemaArray,
  invoiceSchema,
  invoiceTemplateSchema,
  saveTemplateArraySchema,
  templateReplaceRecordSchema,
  SummarySettlementSchema,
  SummaryCustomersSchema,
  ChartDataSchema,
  CalendarSchema,
  MonthlySettlementDetailSchema,
} from '@/schema/api/settlement.schema';
import { useRequestStore } from '@/store/useRequestStore';
import { wrapQueryPromise, wrapMutationPromise, blodDownloadWrapQueryPromise } from '@/utils/api';
import { SchemaType } from '@/utils/types';

function delay(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export const useInvoiceTemplateList = () => {
  const queryKey = computed(() => [Apis.SETTLEMENT__$1__INVOICE_TEMPLATE_LIST]);
  const { $get } = useRequestStore();

  return useQuery({
    queryKey,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: 0,
    queryFn: () => {
      return wrapQueryPromise($get(Apis.SETTLEMENT__$1__INVOICE_TEMPLATE_LIST, {}), invoiceTemplateSchema);
    },
  });
};

// 정산 발송 템플릿 예약어 치환 리스트
export const useInvoiceTemplateReplaceListMutation = (options?: { onSuccess?: () => void; onError?: (err: Error) => void }) => {
  const { $post } = useRequestStore();

  const m = useMutation<
    SchemaType<typeof templateReplaceRecordSchema>, // 반환 타입
    Error, // 에러 타입
    { uuids: string[] } // 변수 타입
  >({
    mutationFn: async uuidList => {
      const result = (await wrapMutationPromise($post(Apis.SETTLEMENT__POST_INVOICE_TEMPLATE_REPLACE_LIST, uuidList))) as SchemaType<
        typeof templateReplaceRecordSchema
      >;
      if (!result) {
        throw new Error('No data received');
      }
      return result;
    },
    onSuccess: () => {
      options?.onSuccess && options?.onSuccess();
      m.reset();
    },
    onError: err => {
      options?.onError && options.onError(err);
      m.reset();
    },
  });

  return m;
};
// 정산 템플릿 저장
export const useSaveInvoiceTemplateListMutation = (options?: { onSuccess?: () => void; onError?: (err: Error) => void }) => {
  const { $post } = useRequestStore();

  const m = useMutation({
    mutationFn: async (data: SchemaType<typeof saveTemplateArraySchema>) => {
      await delay(1000);
      return wrapMutationPromise($post(Apis.SETTLEMENT__$1__INVOICE_TEMPLATE_LIST, data));
    },
    onSuccess: () => {
      options?.onSuccess && options?.onSuccess();
      m.reset();
    },
    onError: err => {
      options?.onError && options.onError(err);
      m.reset();
    },
  });

  return m;
};

export const useCompanyPaymentList = () => {
  const queryKey = computed(() => [Apis.SETTLEMENT__COMPANY_PAYMENTS]);
  const { $get } = useRequestStore();

  return useQuery({
    queryKey,
    staleTime: 0,
    queryFn: () => {
      return wrapQueryPromise($get(Apis.SETTLEMENT__COMPANY_PAYMENTS, {}), companyPaymentSchemaArray);
    },
  });
};

export const useModifyCompanyPaymentReceivedMutation = (options?: { onSuccess?: () => void; onError?: (err: Error) => void }) => {
  const { $patch } = useRequestStore();
  const m = useMutation({
    mutationFn(data: { company_payment_ids: string[]; received: boolean }) {
      return wrapMutationPromise($patch(Apis.SETTLEMENT__COMPANY_PAYMENTS__RECEIVED, data));
    },
    onSuccess: options?.onSuccess,
    onError: err => {
      options?.onError && options.onError(err);
      m.reset();
    },
  });

  return m;
};

export const usePostInvoicesMutation = (options?: { onSuccess?: () => void; onError?: (err: Error) => void }) => {
  const { $post } = useRequestStore();
  const m = useMutation({
    mutationFn(data: SchemaType<typeof invoiceSchema>) {
      return wrapMutationPromise($post(Apis.SETTLEMENT__POST_INVOICE_LIST, data));
    },
    onSuccess: options?.onSuccess,
    onError: err => {
      options?.onError && options.onError(err);
      m.reset();
    },
  });

  return m;
};

export const useDownloadInvoicesPdfMutation = (options?: { onSuccess?: () => void; onError?: (err: Error) => void }) => {
  const { $get } = useRequestStore();

  const m = useMutation({
    mutationFn(data: { uuid: string | null }) {
      if (!data.uuid) return Promise.reject(null);
      return blodDownloadWrapQueryPromise(
        $get(Apis.SETTLEMENT__DOWNLOAD_PDF_S3_VERSION, {
          responseType: 'blob',
          params: { uuid: data.uuid },
        }),
      );
    },
    onSuccess: () => {
      options?.onSuccess && options?.onSuccess();
      m.reset();
    },
    onError: err => {
      options?.onError && options.onError(err);
      m.reset();
    },
  });

  return m;
};

export const useSettlementPdfInfo = (id: string | null) => {
  const queryKey = computed(() => [Apis.SETTLEMENT__$1__SETTLEMENT_PDF_INFO]);
  const { $get } = useRequestStore();

  return useQuery({
    queryKey,
    queryFn: () => {
      if (!id) return Promise.reject(null);
      return wrapQueryPromise($get(Apis.SETTLEMENT__$1__SETTLEMENT_PDF_INFO.replace(IdLiteral.COMPANY_PAYMENTS_ID, id), {}), PdfInfoSchema);
    },
  });
};

export const useSettlementPdfInfoMutation = (options?: { onSuccess?: () => void; onError?: (err: Error) => void }) => {
  const { $get } = useRequestStore();

  const m = useMutation({
    mutationFn(data: { id: string | null }) {
      if (!data.id) return Promise.reject(null);
      return wrapMutationPromise($get(Apis.SETTLEMENT__$1__SETTLEMENT_PDF_INFO.replace(IdLiteral.COMPANY_PAYMENTS_ID, data.id), {}));
    },
    onSuccess: () => {
      options?.onSuccess && options?.onSuccess();
      m.reset();
    },
    onError: err => {
      options?.onError && options.onError(err);
      m.reset();
    },
  });

  return m;
};

export const useSettlementContract = (id: Ref<string | null>) => {
  const queryKey = computed(() => [Apis.SETTLEMENT__$1__GET_SETTLEMENT_INFO, id.value + '']);

  const { $get } = useRequestStore();
  return useQuery({
    queryKey,
    queryFn: () => {
      if (!id.value) return Promise.reject(null);
      return wrapQueryPromise(
        $get(Apis.SETTLEMENT__$1__GET_SETTLEMENT_INFO.replace(IdLiteral.PLANT_ID, id.value)),
        settlementContractSchema,
      );
    },
    enabled: computed(() => !!id.value),
  });
};

// 이 훅 잘못작성 되었습니다. useSettlementContract를 사용하길 권장 합니다.
// TODO: SettlementInfoRegistModifyModal에서 결합성 높게 사용하고 있어 바로 삭제는 어렵고 나중에 일정 좀 더 잡고 useSettlementContract로 변경이 필요합니다.
export const useGetSettlementContract = (id: string | null) => {
  const selectedId = id ? id : '-1';
  const queryKey = computed(() => [Apis.SETTLEMENT__$1__GET_SETTLEMENT_INFO, id]);

  const { $get } = useRequestStore();
  return useQuery({
    queryKey,
    queryFn: () => {
      if (!id) return Promise.reject(null);
      return wrapQueryPromise(
        $get(Apis.SETTLEMENT__$1__GET_SETTLEMENT_INFO.replace(IdLiteral.PLANT_ID, selectedId)),
        settlementContractSchema,
      );
    },
    enabled: !!selectedId,
  });
};

export const useHaezoomNumberCheckMutation = (options?: { onSuccess?: () => void; onError?: (err: Error) => void }) => {
  const { $get } = useRequestStore();
  const m = useMutation({
    mutationFn(hzNumber: string | null) {
      if (!hzNumber) return Promise.reject(null);
      return wrapMutationPromise($get(Apis.SETTLEMENT__$1_GET_HAEZOOM_NUMBER_CHECK.replace(IdLiteral.HAEZOOM_NUMBER, hzNumber)));
    },
    onSuccess: options?.onSuccess,
    onError: err => {
      options?.onError && options.onError(err);
      m.reset();
    },
  });

  return m;
};

export const useModifySettlementInfoMutation = (
  id: string | null,
  options?: {
    onSuccess?: () => void;
    onError?: (err: Error) => void;
  },
) => {
  const { $patch, $delete } = useRequestStore();
  const m = useMutation({
    mutationFn(data: {
      payload: SchemaType<typeof postSettlementContractSchema>;
      deleteList: {
        members: number[];
      };
    }) {
      if (!id) return Promise.reject(null);

      data.deleteList.members.map(idx =>
        wrapMutationPromise($delete(Apis.SETTLEMENT__$1_DELETE_MEMBERS.replace(IdLiteral.MEMBER_ID, `${idx}`))),
      );
      return wrapMutationPromise($patch(Apis.SETTLEMENT__$1__GET_SETTLEMENT_INFO.replace(IdLiteral.PLANT_ID, id), data.payload));
    },
    onSuccess: options?.onSuccess,
    onError: err => {
      options?.onError && options.onError(err);
      m.reset();
    },
  });

  return m;
};

export const useCreateSettlementInfoMutation = (options?: { onSuccess?: () => void; onError?: (err: Error) => void }) => {
  const { $post } = useRequestStore();
  const m = useMutation({
    mutationFn(data: SchemaType<typeof postSettlementContractSchema>) {
      return wrapMutationPromise($post(Apis.SETTLEMENT__POST_CREATE_SETTLEMENTINFO, data));
    },
    onSuccess: options?.onSuccess,
    onError: err => {
      options?.onError && options.onError(err);
      m.reset();
    },
  });

  return m;
};

export const useGetSettlementPlants = () => {
  const queryKey = computed(() => [Apis.SETTLEMENT__GET_PLANTS]);

  const { $get } = useRequestStore();
  return useQuery({
    queryKey,
    queryFn: () => {
      return wrapQueryPromise($get(Apis.SETTLEMENT__GET_PLANTS), plantsLocationArray);
    },
  });
};

// 대시보드 INFO
export const useGetSettlementDashboardSummary = (groupId: Ref<string>) => {
  const { $get } = useRequestStore();

  const results = useQueries({
    queries: [
      {
        queryKey: [Apis.SETTLEMENT__GET_SETTLEMENT_DASHBOARD_SUMMARY_SETTLEMENTS, groupId],
        queryFn: () =>
          wrapQueryPromise(
            $get(Apis.SETTLEMENT__GET_SETTLEMENT_DASHBOARD_SUMMARY_SETTLEMENTS, {
              params: { resource_group: groupId.value === '전체' ? '' : groupId.value },
            }),
            SummarySettlementSchema,
          ),
        enabled: !!groupId.value,
      },
      {
        queryKey: [Apis.SETTLEMENT__GET_SETTLEMENT_DASHBOARD_SUMMARY_CUSTOMERS, groupId],
        queryFn: () =>
          wrapQueryPromise(
            $get(Apis.SETTLEMENT__GET_SETTLEMENT_DASHBOARD_SUMMARY_CUSTOMERS, {
              params: { resource_group: groupId.value === '전체' ? '' : groupId.value },
            }),
            SummaryCustomersSchema,
          ),
        enabled: !!groupId.value,
      },
    ],
  });

  const combinedData = computed(() => {
    const [summaryResult, customersResult] = results.value as [
      QueryObserverResult<SchemaType<typeof SummarySettlementSchema>>,
      QueryObserverResult<SchemaType<typeof SummaryCustomersSchema>>,
    ];

    if (!summaryResult.isLoading && !customersResult.isLoading && summaryResult.data && customersResult.data) {
      return {
        settlement: summaryResult.data.settlements,
        customerStatus: customersResult.data.current_customers,
      };
    }

    return null;
  });

  return {
    data: combinedData,
    isLoading: computed(() => results.value.some(result => result.isLoading)),
    isError: computed(() => results.value.some(result => result.isError)),
    error: computed(() => results.value.find(result => result.error)?.error || null),
  };
};

export const useGetSettlementDashboardHeaderData = () => {
  const queryKey = computed(() => [Apis.SETTLEMENT__GET_SETTLEMENT_DASHBOARD_HEAER]);

  const { $get } = useRequestStore();
  return useQuery({
    queryKey,
    queryFn: () => {
      return wrapQueryPromise($get(Apis.SETTLEMENT__GET_SETTLEMENT_DASHBOARD_HEAER), SettlementHeaderSchema);
    },
  });
};

export const useGetSettlementDashboardMonthlySettlement = (date: Ref<moment.Moment>, groupId: Ref<string>) => {
  const queryKey = computed(() => [Apis.SETTLEMENT__GET_SETTLEMENT_DASHBOARD_MONTHLY_SETTLEMENT, date.value, groupId.value]);

  const { $get } = useRequestStore();
  return useQuery({
    queryKey,
    queryFn: () => {
      return wrapQueryPromise(
        $get(Apis.SETTLEMENT__GET_SETTLEMENT_DASHBOARD_MONTHLY_SETTLEMENT, {
          params: { date: moment(date.value).format('YYYY-MM'), resource_group: groupId.value === '전체' ? '' : groupId.value },
        }),
        MonthlySettlementDetailSchema,
      );
    },
  });
};

// 대시보드 chart
export const useGetSettlementDashboardChartData = (date: Ref<moment.Moment>, groupId: Ref<string>, chartType: Ref<'daily' | 'monthly'>) => {
  const queryKey = computed(() => [
    Apis.SETTLEMENT__GET_SETTLEMENT_DASHBOARD_CHART,
    date.value.format('YYYY-MM'),
    groupId.value,
    chartType.value,
  ]);

  const { $get } = useRequestStore();
  return useQuery({
    queryKey,
    queryFn: () => {
      return wrapQueryPromise(
        $get(Apis.SETTLEMENT__GET_SETTLEMENT_DASHBOARD_CHART, {
          params: {
            type: chartType.value,
            resource_group: groupId.value === '전체' ? '' : groupId.value,
            date: date.value.format('YYYY-MM'),
          },
        }),
        ChartDataSchema,
      );
    },
  });
};

export const useGetSettlementDashboardChartExcelData = (options?: { onSuccess?: () => void; onError?: (err: Error) => void }) => {
  const { $get } = useRequestStore();

  const m = useMutation({
    mutationFn: async ({
      date,
      groupId,
      chartType,
    }: {
      date: Ref<moment.Moment>;
      groupId: Ref<string>;
      chartType: Ref<'daily' | 'monthly'>;
    }) => {
      if (!date.value || !chartType.value || !groupId.value) return Promise.reject(null);

      return blodDownloadWrapQueryPromise(
        $get(Apis.SETTLEMENT__GET_SETTLEMENT_DASHBOARD_CHART_EXCECL, {
          responseType: 'blob',
          params: {
            type: chartType.value,
            resource_group: groupId.value === '전체' ? '' : groupId.value,
            date: moment(date.value).format('YYYY-MM'),
          },
        }),
      );
    },
    onSuccess: () => {
      options?.onSuccess && options?.onSuccess();
      m.reset();
    },
    onError: err => {
      options?.onError && options.onError(err);
      m.reset();
    },
  });

  return m;
};

// 대시보드 정산일 날짜
export const useGetSettlementDashboardCalendar = (date: Ref<moment.Moment>) => {
  const queryKey = computed(() => [Apis.SETTLEMENT__GET_SETTLEMENT_DASHBOARD_CALENDAR, date.value.format('YYYY-MM')]);

  const { $get } = useRequestStore();

  return useQuery({
    queryKey,
    queryFn: () => {
      return wrapQueryPromise(
        $get(Apis.SETTLEMENT__GET_SETTLEMENT_DASHBOARD_CALENDAR, { params: { date: date.value.format('YYYY-MM') } }),
        CalendarSchema,
      );
    },
  });
};
